/**
 * useCalendars Hook
 *
 * Fetches and manages the user's calendar collections.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { api, CalendarCollectionDetail, CalendarCollectionInput } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

export function useCalendars() {
  const [calendars, setCalendars] = useState<CalendarCollectionDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCalendars = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.listCalendarCollections();
      setCalendars(result.calendars);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load calendars');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  const createCalendar = useCallback(async (input: CalendarCollectionInput) => {
    const created = await api.createCalendarCollection(input);
    setCalendars((prev) => [...prev, created]);
    return created;
  }, []);

  const updateCalendar = useCallback(
    async (id: string, input: Partial<CalendarCollectionInput>) => {
      const updated = await api.updateCalendarCollection(id, input);
      setCalendars((prev) => prev.map((c) => (c.id === id ? updated : c)));
      return updated;
    },
    [],
  );

  const deleteCalendar = useCallback(async (id: string, deleteEvents = false) => {
    await api.deleteCalendarCollection(id, deleteEvents);
    setCalendars((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const toggleVisibility = useCallback(
    async (id: string) => {
      const calendar = calendars.find((c) => c.id === id);
      if (!calendar) return;
      try {
        await updateCalendar(id, { isVisible: !calendar.isVisible });
      } catch {
        toast({
          title: 'Error',
          description: 'Failed to toggle calendar visibility',
          variant: 'destructive',
        });
      }
    },
    [calendars, updateCalendar],
  );

  const visibleCalendarIds = useMemo(
    () => calendars.filter((c) => c.isVisible).map((c) => c.id),
    [calendars],
  );

  return {
    calendars,
    loading,
    error,
    fetchCalendars,
    createCalendar,
    updateCalendar,
    deleteCalendar,
    toggleVisibility,
    visibleCalendarIds,
  };
}
